import React, { useState, useEffect, useMemo } from 'react'
import axios from 'axios'
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChartLine } from '@fortawesome/free-solid-svg-icons'
import useConfirm from '../hooks/Dialog'
import { convertTimestampToTime, convertTimestampToDate, convertSecondsToDuration } from './../utils'
import { IndeterminateCheckbox, EditCell } from './Table'
import Loading from './Loading'

const columnHelper = createColumnHelper()

const tableStyle = {
  height: "32.35em",
  display: "block",
  overflowY: "auto"
}

export default function ReportTable({facility, selectedReport, selectReport, user}) {
  const { confirm } = useConfirm()
  const [index, setIndex] = useState(0)
  const [limit, setLimit] = useState(10)
  const [search, setSearch] = useState("")
  const [data, setData] = useState([])
  const [isDirty, setDirty] = useState(true)
  const [pageCount, setPageCount] = useState(0)
  const [status, setStatus] = useState("fetched")
  const [rowSelection, setRowSelection] = useState({})
  const [columnVisibility, setColumnVisibility] = useState({})
  const isAdmin = user.role === "admin"

  let columns = useMemo(() => {
    let cols = [
      columnHelper.accessor("id", {
        header: ({ table }) => (
          <IndeterminateCheckbox
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row }) => (
          <div className="px-1">
            <IndeterminateCheckbox
              {...{
                checked: row.getIsSelected(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </div>
        ),
        }),
        columnHelper.accessor('name', {
          header: () => 'Tag',
          cell: info =>
            <span className='text-blue' style={{cursor: "pointer"}} onClick={() => selectReport(info.row.original)}>
              <FontAwesomeIcon className='mr-1' icon={faChartLine} />{info.getValue()}
            </span>,
        }),
        columnHelper.accessor('start_time', {
          header: () => 'Date',
          cell: info => <span>{convertTimestampToDate(info.getValue())} {convertTimestampToTime(info.getValue())}</span>,
        }),
        columnHelper.accessor('duration', {
          header: () => 'Duration',
          cell: info => <span>{convertSecondsToDuration(info.getValue())}</span>,
        }),
        columnHelper.accessor('comment', {
          header: () => 'Comment',
          cell: info => (isAdmin ?
            <EditCell updateFunc={(row, value) => {
              const url = `/api/v1/report/${row.name}`
              return axios({ url, method: 'PUT', params: { organisation: row.organisation }, data: { comment: value || "" }})
            }} {...info}/>
          : <span>{info.getValue()}</span>),
        })
      ]
      if (isAdmin) {
        cols.push(columnHelper.accessor('upload', {
          header: () => 'Upload',
          cell: info => <span>{info.getValue() ? "Yes" : "No"}</span>,
        }))
      }
      return cols
  }, [isAdmin, selectReport])

  const showConfirmDownload = async () => {
    if (rowSelection.length === 0) return
    const reports = data.map((r, i) => rowSelection[i] ? r : undefined).filter(e => e !== undefined)
    const isConfirmed = await confirm('Download reports', `Do you want to download ${reports.length} report(s)?`)
    if (isConfirmed) {
      setStatus("downloading")
      let errors = []
      for (const report of reports) {
        const url = `/api/v1/report/${report.name}/download`
        try {
          let response = await axios.get(url, { params: { organisation: report.organisation } })
          let blob = new Blob([response.data])
          let downloadUrl = window.URL.createObjectURL(blob)
          let a = document.createElement("a")
          if (typeof a.download === "undefined") {
            window.location.href = downloadUrl
          } else {
            a.href = downloadUrl
            a.download = report.name + ".csv"
            document.body.appendChild(a)
            a.click()
          }
        }
        catch (error) {
          console.log(`Failed to download report ${report.name}: ${error}`)
          errors.push(report.tag)
        }
      }
      if (errors.length > 0) {
        await confirm(
          'Download reports', 'An error occurred while trying to download ' + errors,
          false
        )
      }
      setRowSelection({})
      setDirty(true)
    }
  }

  const showConfirmDelete = async () => {
    if (rowSelection.length === 0) return
    const reports = data.map((r, i) => rowSelection[i] ? r : undefined).filter(e => e !== undefined)
    const isConfirmed = await confirm('Delete reports', `Do you want to delete ${reports.length} report(s)?`)
    if (isConfirmed) {
      setStatus("fetching")
      for (const report of reports) {
        try {
          await axios.delete(`/api/v1/report/${report.name}`, { params: { organisation: report.organisation } })
        }
        catch (error) {
          if (error.response.status === 404) {
            console.log(`report ${report.name} not found`)
          }
          else {
            console.log(`error deleting report ${report.name}`)
          }
        }
      }
      setDirty(true)
    }
  }

  useEffect(() => {
    setDirty(true)
  }, [facility, index, limit, search])

  useEffect(() => {
    if (isDirty) {
      const fetchReports = async () => {
        const params = { facility: facility, order_by: "-start_time", index: index * limit, limit }
        if (search) params.search = search
        setStatus("fetching")
        try {
          const rsp = await axios.get("/api/v1/report/", { params })
          setData(rsp.data.result)
          setPageCount(rsp.data.page_count)
          if (rsp.data.page_count > 0) {
            setIndex(Math.min(index, rsp.data.page_count - 1))
          }
          setRowSelection({})
          setDirty(false)
          setStatus("fetched")
        }
        catch (e) {
          setStatus("error")
          console.log(e)
        }
      }
      fetchReports()
    }
  }, [isDirty, facility, index, limit, search])

  const table = useReactTable({
    columns,
    data,
    state: { rowSelection, columnVisibility },
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <>
      <div className='card-header bg-transparent'>
        <div className='row align-items-center'>
          <div className='col'>
            <h6 className='text-uppercase text-muted ls-1 mb-1'>{facility}</h6>
            <h2 className="mb-0">Reports</h2>
          </div>
           <div className='col-5'>
           <textarea
            className='form-control'
            rows={1}
            resize='none'
            placeholder='search'
            value={search}
            onChange={e => setSearch(e.target.value)}
           />
          </div>
          <div className='col-auto'>
            <div className='btn btn-primary' onClick={showConfirmDownload}>Download</div>
          </div>
          <div className='col-auto'>
            {isAdmin && <div className='btn btn-primary' onClick={showConfirmDelete}>Delete</div>}
          </div>
        </div>
      </div>
      <div className='d-flex flex-column' style={tableStyle}>
        <div>
          {status !== "fetched" ?
          <Loading/> :
          <table className="table table-hover table-flush align-items-center">
            <thead className="thead-light">
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map(row => (
                <tr className={row.original.id === selectedReport?.id ? "table-light" : ""} key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>}
          <div className='mt-auto container-fluid row'>
            <div className='col px-1 pt-2'>
              <span>{status === "fetching" ? "Loading..." : status === "downloading" && "Downloading..."}</span>
            </div>
            <div className='col px-1 pt-2'>
              <select
               className='custom-select custom-select-sm'
               value={limit}
               onChange={e => setLimit(Number(e.target.value))}
              >
                {[10, 20, 50, 100].map(pageSize => <option key={pageSize} value={pageSize}>Show {pageSize}</option>)}
              </select>
            </div>
            <div className="col-auto py-0">
              <ul className='pagination justify-content-end'>
                <span className='pt-2 pr-1'>Page{' '}</span>
                <strong className='pt-2 pr-2'>
                  {index + 1} of {pageCount}
                </strong>
                <li className='page-item' onClick={() => setIndex(0)}>
                  <button className='page-link px-2'>{'<<'}</button>
                </li>
                {' '}
                <li className='page-item' onClick={() => setIndex(Math.max(index - 1, 0))}>
                  <button className='page-link px-2'>{'<'}</button>
                </li>
                {' '}
                <li className='page-item' onClick={() => setIndex(Math.min(index + 1, pageCount - 1))}>
                  <button className='page-link px-2'>{'>'}</button>
                </li>
                {' '}
                <li className='page-item' onClick={() => setIndex(pageCount - 1)}>
                  <button className='page-link px-2'>{'>>'}</button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}